/**
 * Esqueleto do app.
 *
 * Um único menu, agrupado por seção, desenhado em dois lugares: barra lateral
 * fixa a partir de 768px e gaveta aberta pelo ☰ no celular. As sub-telas
 * (Carteira, Inadimplentes, Controle, Mapa...) são itens do menu, não abas
 * dentro da página — assim só existe um jeito de navegar, o mesmo em qualquer
 * tela, e quem tem pouca intimidade com o sistema não precisa descobrir que
 * havia abas escondidas no topo.
 *
 * No celular, os itens têm 48px de altura e texto maior: é o tamanho mínimo
 * para acertar com o polegar andando na rua.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  ClipboardCheck,
  CloudOff,
  Home,
  ListChecks,
  LogOut,
  Map as IconeMapa,
  Menu as IconeMenu,
  Monitor,
  Moon,
  Sun,
  UserCog,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useAuth, type Papel } from '../../contexts/AuthContext.tsx';
import { useTema } from '../../contexts/ThemeContext.tsx';
import { cx } from '../../lib/cx.ts';
import { iniciais } from '../../lib/format.ts';
import { useDadosGuardados } from '../../lib/pwa.ts';

interface ItemMenu {
  para: string;
  rotulo: string;
  Icone: typeof BarChart3;
  papeis?: Papel[];
  /** Casa só o caminho exato — para o item que é a raiz da seção. */
  fim?: boolean;
}

interface GrupoMenu {
  titulo?: string;
  itens: ItemMenu[];
}

const FINANCEIRO: Papel[] = ['admin', 'diretor', 'gerente_regional', 'financeiro'];
const RASTREAMENTO: Papel[] = ['admin', 'diretor', 'rastreamento'];

const MENU: GrupoMenu[] = [
  {
    itens: [
      { para: '/inicio', rotulo: 'Início', Icone: Home },
      { para: '/vendas', rotulo: 'Vendas', Icone: BarChart3 },
    ],
  },
  {
    titulo: 'Financeiro',
    itens: [
      { para: '/financeiro', rotulo: 'Carteira', Icone: Wallet, papeis: FINANCEIRO, fim: true },
      {
        para: '/financeiro/inadimplentes',
        rotulo: 'Inadimplência',
        Icone: AlertTriangle,
        papeis: [...FINANCEIRO, 'consultor'],
      },
      {
        para: '/financeiro/revistoria',
        rotulo: 'Pendente revistoria',
        Icone: ClipboardCheck,
        papeis: [...FINANCEIRO, 'pos_vendas', 'consultor'],
      },
    ],
  },
  {
    titulo: 'Rastreamento',
    itens: [
      { para: '/rastreamento', rotulo: 'Controle', Icone: ListChecks, papeis: RASTREAMENTO, fim: true },
      {
        para: '/rastreamento/planejamento',
        rotulo: 'Planejamento',
        Icone: BarChart3,
        papeis: RASTREAMENTO,
      },
      { para: '/rastreamento/mapa', rotulo: 'Mapa', Icone: IconeMapa, papeis: RASTREAMENTO },
    ],
  },
  {
    titulo: 'Gestão',
    itens: [
      { para: '/equipe', rotulo: 'Equipe', Icone: Users, papeis: FINANCEIRO },
      { para: '/usuarios', rotulo: 'Usuários', Icone: UserCog, papeis: ['admin', 'diretor'] },
    ],
  },
];

/** Item do menu que corresponde ao caminho — o mais específico vence. */
function itemAtual(pathname: string): { grupo?: string; item: ItemMenu } | null {
  let melhor: { grupo?: string; item: ItemMenu } | null = null;
  for (const g of MENU) {
    for (const item of g.itens) {
      const casa = item.fim ? pathname === item.para : pathname.startsWith(item.para);
      if (casa && (!melhor || item.para.length > melhor.item.para.length)) {
        melhor = { grupo: g.titulo, item };
      }
    }
  }
  return melhor;
}

export default function AppShell() {
  const { pode } = useAuth();
  const [menuAberto, setMenuAberto] = useState(false);
  const fecharMenu = useCallback(() => setMenuAberto(false), []);
  const { pathname } = useLocation();

  const grupos = MENU.map((g) => ({
    ...g,
    itens: g.itens.filter((i) => !i.papeis || pode(...i.papeis)),
  })).filter((g) => g.itens.length > 0);

  // Escolher um destino fecha a gaveta — inclusive tocar no item já ativo.
  useEffect(fecharMenu, [pathname, fecharMenu]);

  return (
    <div className="min-h-dvh bg-[var(--bg)]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[240px] flex-col border-r border-[var(--border)] bg-[var(--bg-subtle)] md:flex">
        <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-5">
          <img
            src="/rondonbens-logo.png"
            alt="Rondonbens"
            className="h-5 w-auto dark:brightness-0 dark:invert"
          />
        </div>
        <Menu grupos={grupos} />
      </aside>

      <Gaveta aberta={menuAberto} onFechar={fecharMenu}>
        <Menu grupos={grupos} grande />
      </Gaveta>

      <div className="md:pl-[240px]">
        <Cabecalho onAbrirMenu={() => setMenuAberto(true)} />
        <AvisoGuardado />
        {/* `overflow-x-clip` é a rede de segurança: se algum cartão ainda
            escapar da largura, ele é recortado em vez de arrastar a página
            inteira para o lado. Não vira contêiner de rolagem, então o
            cabeçalho `sticky` e a folha em portal continuam funcionando. */}
        <main
          className="mx-auto w-full max-w-[1100px] overflow-x-clip px-4 py-6 md:px-8"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)' }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ─────────────────────────── Cabeçalho ───────────────────────────

function Cabecalho({ onAbrirMenu }: { onAbrirMenu: () => void }) {
  const { usuario } = useAuth();
  const { pathname } = useLocation();
  const atual = itemAtual(pathname);
  const titulo = atual?.item.rotulo ?? (pathname.startsWith('/perfil') ? 'Perfil' : 'Hub');

  return (
    <header className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--bg)]">
      <div className="mx-auto flex h-14 max-w-[1100px] items-center gap-1 px-2 md:gap-3 md:px-8">
        <button
          onClick={onAbrirMenu}
          aria-label="Abrir menu"
          className="anel-foco grid size-11 shrink-0 place-items-center rounded-[var(--r-md)] text-[var(--fg)] hover:bg-[var(--surface-hover)] md:hidden"
        >
          <IconeMenu size={22} />
        </button>

        {/* No celular o título diz onde a pessoa está — é o que substitui a
            aba destacada. A seção vem antes, menor, para dar o contexto. */}
        <h1 className="min-w-0 truncate text-[15px] font-medium text-[var(--fg)]">
          {atual?.grupo && (
            <span className="font-normal text-[var(--muted)]">{atual.grupo} · </span>
          )}
          {titulo}
        </h1>

        <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-2">
          <SeletorTema />
          <Link
            to="/perfil"
            title={usuario ? `${usuario.nome} — meu perfil` : 'Meu perfil'}
            className="anel-foco grid size-11 place-items-center rounded-full md:size-8"
          >
            <span className="grid size-8 place-items-center rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[11px] font-medium text-[var(--muted)]">
              {usuario ? iniciais(usuario.nome) : '—'}
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function SeletorTema() {
  const { tema, setTema } = useTema();
  const proximo = tema === 'sistema' ? 'claro' : tema === 'claro' ? 'escuro' : 'sistema';
  const Icone = tema === 'sistema' ? Monitor : tema === 'claro' ? Sun : Moon;

  return (
    <button
      onClick={() => setTema(proximo)}
      aria-label={`Tema: ${tema}. Trocar para ${proximo}.`}
      className="anel-foco grid size-11 place-items-center rounded-[var(--r-md)] text-[var(--muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--fg)] md:size-8"
    >
      <Icone size={16} />
    </button>
  );
}

/**
 * Faixa avisando que a tela mostra o que estava guardado no aparelho — sem
 * ela, o consultor sem sinal tomaria o número de ontem pelo de agora.
 */
function AvisoGuardado() {
  const salvoEm = useDadosGuardados();
  if (!salvoEm) return null;

  const quando = new Date(salvoEm).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  return (
    <div
      role="status"
      className="imprimir-oculto flex items-center justify-center gap-2 border-b border-[var(--border)] bg-[var(--bg-subtle)] px-4 py-2 text-center text-[13px] text-[var(--muted)]"
    >
      <CloudOff size={14} className="shrink-0" />
      Sem conexão — mostrando dados salvos em {quando}
    </div>
  );
}

// ─────────────────────────── Navegação ───────────────────────────

function Menu({ grupos, grande }: { grupos: GrupoMenu[]; grande?: boolean }) {
  const { usuario, sair } = useAuth();

  return (
    <>
      <nav
        aria-label="Navegação principal"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-3"
      >
        {grupos.map((g, i) => (
          <div key={g.titulo ?? i} className="flex flex-col gap-0.5">
            {g.titulo && (
              <p
                className={cx(
                  'px-3 pb-1 font-medium uppercase tracking-wide text-[var(--faint)]',
                  grande ? 'text-xs' : 'text-[11px]',
                )}
              >
                {g.titulo}
              </p>
            )}
            {g.itens.map(({ para, rotulo, Icone, fim }) => (
              <NavLink
                key={para}
                to={para}
                end={fim}
                className={({ isActive }) =>
                  cx(
                    'anel-foco relative flex items-center rounded-[var(--r-md)] px-3 font-medium transition-colors',
                    grande ? 'h-12 gap-3 text-[15px]' : 'h-9 gap-2.5 text-[13px]',
                    isActive
                      ? 'bg-[var(--surface)] text-[var(--fg)] shadow-[0_1px_2px_rgb(0_0_0/0.05)]'
                      : 'text-[var(--muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Marca do item ativo — o único lugar em que o laranja
                        aparece na navegação. */}
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-y-2 left-0 w-0.5 rounded-r bg-[var(--brand)]"
                      />
                    )}
                    <Icone size={grande ? 18 : 16} />
                    {rotulo}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-[var(--border)] p-3">
        <Link
          to="/perfil"
          className="anel-foco mb-1 flex items-center gap-3 rounded-[var(--r-md)] px-3 py-2 hover:bg-[var(--surface-hover)]"
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[11px] font-medium text-[var(--muted)]">
            {usuario ? iniciais(usuario.nome) : '—'}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium text-[var(--fg)]">
              {usuario?.nome}
            </span>
            <span className="block truncate text-xs text-[var(--faint)]">
              <span className="capitalize">{usuario?.papel.replace('_', ' ')}</span> · meu perfil
            </span>
          </span>
        </Link>
        <button
          onClick={() => void sair()}
          className={cx(
            'anel-foco flex w-full items-center gap-2.5 rounded-[var(--r-md)] px-3 font-medium text-[var(--muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]',
            grande ? 'h-12 text-[15px]' : 'h-9 text-[13px]',
          )}
        >
          <LogOut size={grande ? 18 : 16} />
          Sair
        </button>
      </div>
    </>
  );
}

/**
 * Gaveta do celular. Em portal pelo mesmo motivo da Folha: nada no fluxo da
 * página consegue recortá-la.
 */
function Gaveta({
  aberta,
  onFechar,
  children,
}: {
  aberta: boolean;
  onFechar: () => void;
  children: React.ReactNode;
}) {
  const painel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberta) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focoAnterior = document.activeElement as HTMLElement | null;
    painel.current?.focus();

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
      focoAnterior?.focus?.();
    };
  }, [aberta, onFechar]);

  if (!aberta) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        aria-label="Fechar menu"
        onClick={onFechar}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
      />
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex w-[min(320px,85vw)] animate-[deslizar_200ms_ease-out] flex-col border-r border-[var(--border)] bg-[var(--bg-subtle)] shadow-[8px_0_40px_rgb(0_0_0/0.28)] outline-none"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-[var(--border)] pl-5 pr-2">
          <img
            src="/rondonbens-logo.png"
            alt="Rondonbens"
            className="h-5 w-auto dark:brightness-0 dark:invert"
          />
          <button
            onClick={onFechar}
            aria-label="Fechar menu"
            className="anel-foco grid size-11 place-items-center rounded-[var(--r-md)] text-[var(--muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]"
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
